import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaChild, FaGraduationCap, FaClipboardCheck, FaSpinner, FaCheckCircle,
  FaClock, FaArrowRight, FaArrowLeft, FaTimes, FaTrophy, FaExclamationTriangle,
  FaLightbulb, FaStar, FaRocket, FaChartLine, FaCalendarAlt
} from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Test Questions Component
const TestQuestions = ({ questions, answers, setAnswers, onSubmit, onCancel, childInfo, submitting }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const questionsPerPage = 5;
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  
  const currentQuestions = questions.slice(
    currentPage * questionsPerPage,
    (currentPage + 1) * questionsPerPage
  );

  const handleAnswer = (questionNumber, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionNumber]: value
    }));
  };

  const allAnswered = questions.every(q => answers[q.number] !== undefined);
  const currentPageAnswered = currentQuestions.every(q => answers[q.number] !== undefined);

  return (
    <div className="space-y-6">
      {/* Child Info Header */}
      <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-full flex items-center justify-center text-[#0F172A]">
            <FaChild size={20} />
          </div>
          <div>
            <p className="text-white font-bold">{childInfo.name}</p>
            <p className="text-gray-400 text-sm">Age: {childInfo.age} years | {childInfo.class_grade}</p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-[#0F172A] rounded-full h-2">
        <div 
          className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] h-2 rounded-full transition-all duration-300"
          style={{ width: `${(Object.keys(answers).length / questions.length) * 100}%` }}
        />
      </div>
      <p className="text-gray-400 text-sm text-center">
        {Object.keys(answers).length} of {questions.length} questions answered
      </p>

      {/* Questions */}
      <div className="space-y-4">
        {currentQuestions.map((q) => (
          <div 
            key={q.number} 
            className="bg-[#0F172A] rounded-xl p-5 border border-[#2D5A8F] hover:border-[#FFD700]/50 transition-all"
          >
            <p className="text-white font-medium mb-4">
              <span className="text-[#FFD700] mr-2">{q.number}.</span>
              {q.question}
            </p>
            <div className="space-y-2">
              {q.options.map((opt, idx) => (
                <label 
                  key={idx}
                  className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                    answers[q.number] === opt.value
                      ? 'bg-[#FFD700]/20 border-2 border-[#FFD700]'
                      : 'bg-[#1A2942] border-2 border-transparent hover:bg-[#1E3A5F]'
                  }`}
                >
                  <input
                    type="radio"
                    name={`q${q.number}`}
                    value={opt.value}
                    checked={answers[q.number] === opt.value}
                    onChange={() => handleAnswer(q.number, opt.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                    answers[q.number] === opt.value
                      ? 'border-[#FFD700] bg-[#FFD700]'
                      : 'border-gray-500'
                  }`}>
                    {answers[q.number] === opt.value && (
                      <FaCheckCircle className="text-[#0F172A] text-xs" />
                    )}
                  </div>
                  <span className={`text-sm ${answers[q.number] === opt.value ? 'text-white' : 'text-gray-300'}`}>
                    {opt.text}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4">
        <button
          onClick={currentPage === 0 ? onCancel : () => setCurrentPage(p => p - 1)}
          className="flex items-center px-4 py-2 text-gray-400 hover:text-white transition-colors"
        >
          <FaArrowLeft className="mr-2" />
          {currentPage === 0 ? 'Cancel' : 'Previous'}
        </button>

        <span className="text-gray-400 text-sm">
          Page {currentPage + 1} of {totalPages}
        </span>

        {currentPage < totalPages - 1 ? (
          <button
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={!currentPageAnswered}
            className={`flex items-center px-6 py-2 rounded-lg font-medium transition-all ${
              currentPageAnswered
                ? 'bg-[#FFD700] text-[#0F172A] hover:bg-[#FFA500]'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            Next
            <FaArrowRight className="ml-2" />
          </button>
        ) : (
          <button
            onClick={onSubmit}
            disabled={!allAnswered || submitting}
            className={`flex items-center px-6 py-2 rounded-lg font-medium transition-all ${
              allAnswered && !submitting
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:from-green-600 hover:to-emerald-700'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <FaCheckCircle className="mr-2" />
                Submit Test
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// Test Results Component
const TestResults = ({ result }) => {
  const getScoreColor = (category) => {
    switch (category) {
      case 'proactive': return 'text-green-400';
      case 'aware': return 'text-blue-400';
      case 'at_risk': return 'text-orange-400';
      case 'unprepared': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getScoreIcon = (category) => {
    switch (category) {
      case 'proactive': return <FaTrophy className="text-green-400" />;
      case 'aware': return <FaLightbulb className="text-blue-400" />;
      case 'at_risk': return <FaExclamationTriangle className="text-orange-400" />;
      case 'unprepared': return <FaRocket className="text-red-400" />;
      default: return <FaStar className="text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Score Card */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#FFD700]/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-[#FFD700]/20 rounded-full flex items-center justify-center">
              {getScoreIcon(result.interpretation_category)}
            </div>
            <div>
              <p className="text-white font-bold">{result.child_name}'s Results</p>
              <p className="text-gray-400 text-sm">Age {result.child_age} | {result.child_class}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold text-[#FFD700]">{result.total_score}</p>
            <p className="text-gray-400 text-sm">out of {result.max_score}</p>
          </div>
        </div>

        {/* Score Bar */}
        <div className="bg-[#0F172A] rounded-full h-4 mb-4">
          <div 
            className="h-4 rounded-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] transition-all"
            style={{ width: `${(result.total_score / result.max_score) * 100}%` }}
          />
        </div>

        {/* Interpretation */}
        <div className={`p-4 rounded-lg bg-[#0F172A] border-l-4 ${
          result.interpretation_category === 'proactive' ? 'border-green-500' :
          result.interpretation_category === 'aware' ? 'border-blue-500' :
          result.interpretation_category === 'at_risk' ? 'border-orange-500' : 'border-red-500'
        }`}>
          <p className={`font-bold mb-2 ${getScoreColor(result.interpretation_category)}`}>
            {result.interpretation_category === 'proactive' && 'Proactive Planner'}
            {result.interpretation_category === 'aware' && 'Aware but Needs Action'}
            {result.interpretation_category === 'at_risk' && 'At Risk — Act Now'}
            {result.interpretation_category === 'unprepared' && 'Unprepared but Not Too Late'}
          </p>
          <p className="text-gray-300 text-sm">{result.interpretation}</p>
        </div>
      </div>

      {/* Recommendation Card */}
      <div className={`rounded-xl p-6 border ${
        result.recommendation_service === 'shiksha_sankalp' 
          ? 'bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-purple-500/30'
          : 'bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border-blue-500/30'
      }`}>
        <div className="flex items-center space-x-3 mb-4">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            result.recommendation_service === 'shiksha_sankalp' 
              ? 'bg-purple-500/20'
              : 'bg-blue-500/20'
          }`}>
            <FaGraduationCap className={
              result.recommendation_service === 'shiksha_sankalp' 
                ? 'text-purple-400'
                : 'text-blue-400'
            } size={20} />
          </div>
          <div>
            <p className="text-white font-bold text-lg">
              {result.recommendation_service === 'shiksha_sankalp' 
                ? 'Recommended: Shiksha Sankalp'
                : 'Recommended: Goal Manthan'}
            </p>
            <p className={`text-sm ${
              result.recommendation_service === 'shiksha_sankalp' 
                ? 'text-purple-300'
                : 'text-blue-300'
            }`}>FREE Service Available</p>
          </div>
        </div>
        <p className="text-gray-300 text-sm mb-4">{result.recommendation_details}</p>
      </div>

      {/* Next Steps */}
      {result.next_steps && result.next_steps.length > 0 && (
        <div className="bg-[#0F172A] rounded-xl p-6 border border-[#2D5A8F]">
          <h4 className="text-white font-bold mb-4 flex items-center">
            <FaChartLine className="text-[#FFD700] mr-2" />
            Your Next Steps
          </h4>
          <div className="space-y-3">
            {result.next_steps.map((step, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-[#FFD700]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[#FFD700] text-xs font-bold">{idx + 1}</span>
                </div>
                <p className="text-gray-300 text-sm">{step}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const ChildFutureReadyTest = () => {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [myTests, setMyTests] = useState([]);
  const [showTest, setShowTest] = useState(false);
  const [answers, setAnswers] = useState({});
  const [childInfo, setChildInfo] = useState({ name: '', age: '', class_grade: '' });
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [questionsRes, testsRes] = await Promise.all([
        axios.get(`${API}/child-future-test/questions`, { headers }),
        axios.get(`${API}/child-future-test/my-tests`, { headers })
      ]);
      
      setQuestions(questionsRes.data || []);
      setMyTests(testsRes.data || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = () => {
    if (!childInfo.name || !childInfo.age || !childInfo.class_grade) {
      alert('Please fill in all child details');
      return;
    }
    setShowTest(true);
    setAnswers({});
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const submission = {
        child_name: childInfo.name,
        child_age: parseInt(childInfo.age),
        child_class: childInfo.class_grade,
        answers: Object.entries(answers).map(([qNum, value]) => ({
          question_number: parseInt(qNum),
          answer_value: value
        }))
      };

      await axios.post(`${API}/child-future-test/submit`, submission, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccessMessage('Test submitted successfully! Your advisor will review and share results soon.');
      setShowTest(false);
      setAnswers({});
      setChildInfo({ name: '', age: '', class_grade: '' });
      fetchData(); // Refresh tests list
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      alert('Failed to submit test. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-center h-32">
          <FaSpinner className="animate-spin text-[#FFD700] text-2xl" />
        </div>
      </div>
    );
  }

  // If viewing a result
  if (selectedResult) {
    return (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white flex items-center">
            <FaClipboardCheck className="text-[#FFD700] mr-3" />
            Test Results
          </h3>
          <button 
            onClick={() => setSelectedResult(null)}
            className="text-gray-400 hover:text-white"
          >
            <FaTimes size={20} />
          </button>
        </div>
        <TestResults result={selectedResult} />
      </div>
    );
  }

  // If taking the test
  if (showTest) {
    return (
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white flex items-center">
            <FaClipboardCheck className="text-[#FFD700] mr-3" />
            Is Your Child Future Ready?
          </h3>
        </div>
        <TestQuestions 
          questions={questions}
          answers={answers}
          setAnswers={setAnswers}
          onSubmit={handleSubmit}
          onCancel={() => setShowTest(false)}
          childInfo={childInfo}
          submitting={submitting}
        />
      </div>
    );
  }

  // Default view - Start test or view results
  return (
    <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#FFA500] rounded-lg flex items-center justify-center">
          <FaChild className="text-[#0F172A]" />
        </div>
        <div>
          <h3 className="text-white font-bold">Is Your Child Future Ready?</h3>
          <p className="text-gray-400 text-sm">A Parent Survey Form</p>
        </div>
      </div>

      <p className="text-gray-300 text-sm mb-4 italic">
        "Finance करेंगे SET, Career बनाएंगे PERFECT"
      </p>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-500/20 border border-green-500 rounded-lg p-4 mb-4">
          <p className="text-green-400 text-sm flex items-center">
            <FaCheckCircle className="mr-2" />
            {successMessage}
          </p>
        </div>
      )}

      {/* Previous Tests */}
      {myTests.length > 0 && (
        <div className="mb-6">
          <p className="text-gray-400 text-sm mb-3">Your Submitted Tests:</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {myTests.map((test) => (
              <div 
                key={test.id}
                className={`p-3 rounded-lg border ${
                  test.status === 'approved' 
                    ? 'bg-green-500/10 border-green-500/30 cursor-pointer hover:bg-green-500/20'
                    : test.status === 'rejected'
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-[#0F172A] border-[#2D5A8F]'
                }`}
                onClick={() => test.status === 'approved' && setSelectedResult(test)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FaChild className={
                      test.status === 'approved' ? 'text-green-400' :
                      test.status === 'rejected' ? 'text-red-400' : 'text-[#FFD700]'
                    } />
                    <div>
                      <p className="text-white text-sm font-medium">{test.child_name}</p>
                      <p className="text-gray-400 text-xs">Age {test.child_age} | {test.child_class}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {test.status === 'approved' ? (
                      <span className="text-green-400 text-xs flex items-center">
                        <FaCheckCircle className="mr-1" />
                        View Results
                      </span>
                    ) : test.status === 'rejected' ? (
                      <span className="text-red-400 text-xs">Needs Resubmission</span>
                    ) : (
                      <span className="text-yellow-400 text-xs flex items-center">
                        <FaClock className="mr-1" />
                        Awaiting Review
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Start New Test */}
      <div className="bg-[#0F172A] rounded-xl p-4 border border-[#2D5A8F]">
        <p className="text-white font-medium mb-4">Start a New Test</p>
        <div className="space-y-3">
          <div>
            <label className="text-gray-400 text-sm block mb-1">Child's Name</label>
            <input
              type="text"
              value={childInfo.name}
              onChange={(e) => setChildInfo(prev => ({ ...prev, name: e.target.value }))}
              className="w-full bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-4 py-2 text-white focus:border-[#FFD700] focus:outline-none"
              placeholder="Enter child's name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-sm block mb-1">Age (years)</label>
              <input
                type="number"
                min="0"
                max="25"
                value={childInfo.age}
                onChange={(e) => setChildInfo(prev => ({ ...prev, age: e.target.value }))}
                className="w-full bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-4 py-2 text-white focus:border-[#FFD700] focus:outline-none"
                placeholder="Age"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm block mb-1">Class/Grade</label>
              <select
                value={childInfo.class_grade}
                onChange={(e) => setChildInfo(prev => ({ ...prev, class_grade: e.target.value }))}
                className="w-full bg-[#1A2942] border border-[#2D5A8F] rounded-lg px-4 py-2 text-white focus:border-[#FFD700] focus:outline-none"
              >
                <option value="">Select Class</option>
                <option value="Pre-school">Pre-school</option>
                <option value="Nursery">Nursery</option>
                <option value="LKG">LKG</option>
                <option value="UKG">UKG</option>
                <option value="Class 1">Class 1</option>
                <option value="Class 2">Class 2</option>
                <option value="Class 3">Class 3</option>
                <option value="Class 4">Class 4</option>
                <option value="Class 5">Class 5</option>
                <option value="Class 6">Class 6</option>
                <option value="Class 7">Class 7</option>
                <option value="Class 8">Class 8</option>
                <option value="Class 9">Class 9</option>
                <option value="Class 10">Class 10</option>
                <option value="Class 11">Class 11</option>
                <option value="Class 12">Class 12</option>
                <option value="College">College</option>
              </select>
            </div>
          </div>
        </div>
        <button
          onClick={handleStartTest}
          disabled={!childInfo.name || !childInfo.age || !childInfo.class_grade}
          className={`w-full mt-4 py-3 rounded-lg font-medium flex items-center justify-center transition-all ${
            childInfo.name && childInfo.age && childInfo.class_grade
              ? 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0F172A] hover:shadow-lg hover:shadow-[#FFD700]/30'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          <FaClipboardCheck className="mr-2" />
          Start Test (10 Questions)
        </button>
      </div>
    </div>
  );
};

export default ChildFutureReadyTest;
